<template>
  <div
    ref="containerRef"
    class="image-viewer"
    :class="{
      'image-viewer--loading': isLoading,
      'image-viewer--zoomed': panZoom.isZoomed,
      'image-viewer--dragging': isDragging,
    }"
    @wheel.prevent="handleWheel"
    @mousedown="handleMouseDown"
    @dblclick="handleDoubleClick"
    @touchstart="handleTouchStart"
    @touchmove="handleTouchMove"
    @touchend="handleTouchEnd"
  >
    <!-- Loading overlay -->
    <div v-if="isLoading" class="image-viewer__loading">
      <v-progress-circular indeterminate size="48" width="3" color="primary" />
    </div>

    <!-- Image container with transitions -->
    <Transition :name="transitionName" mode="out-in">
      <div
        v-if="src"
        :key="src"
        class="image-viewer__image-wrapper"
        :style="imageWrapperStyle"
      >
        <img
          ref="imageRef"
          :src="src"
          :alt="alt"
          class="image-viewer__image"
          @load="handleImageLoad"
          @error="handleImageError"
          draggable="false"
        />
      </div>
    </Transition>

    <!-- Error state -->
    <div v-if="hasError" class="image-viewer__error">
      <v-icon color="error" size="48" :icon="mdiImageBroken" />
      <span class="image-viewer__error-text">Failed to load image</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { mdiImageBroken } from '@mdi/js'
import type { PanZoomState, ImageTransition } from '../types'

const props = withDefaults(
  defineProps<{
    src?: string
    alt?: string
    panZoom: PanZoomState
    transition?: ImageTransition
    transitionDuration?: number
    enableZoom?: boolean
    enablePan?: boolean
  }>(),
  {
    src: undefined,
    alt: '',
    transition: 'fade',
    transitionDuration: 300,
    enableZoom: true,
    enablePan: true,
  },
)

const emit = defineEmits<{
  zoomIn: []
  zoomOut: []
  resetZoom: []
  pan: [deltaX: number, deltaY: number]
  load: [dimensions: { width: number; height: number }]
  error: [error: Error]
}>()

// Refs
const containerRef = ref<HTMLElement | null>(null)
const imageRef = ref<HTMLImageElement | null>(null)

// State
const isLoading = ref(true)
const hasError = ref(false)
const isDragging = ref(false)
const dragStart = ref({ x: 0, y: 0 })
const imageDimensions = ref({ width: 0, height: 0 })

// Touch state for pinch zoom
const lastTouchDistance = ref(0)

// Computed
const transitionName = computed(() => {
  if (props.transition === 'none') return ''
  return `image-${props.transition}`
})

const imageWrapperCursor = computed(() => {
  if (isDragging.value) return 'grabbing'
  if (props.panZoom.isZoomed && props.enablePan) return 'grab'
  return 'default'
})

const imageWrapperStyle = computed(() => ({
  transform: `translate(${props.panZoom.panX}px, ${props.panZoom.panY}px) scale(${props.panZoom.zoom})`,
  transitionDuration: isDragging.value ? '0ms' : `${props.transitionDuration}ms`,
  cursor: imageWrapperCursor.value,
}))

// Watch for src changes
watch(
  () => props.src,
  () => {
    isLoading.value = true
    hasError.value = false
  },
)

// Event handlers
function handleImageLoad(): void {
  isLoading.value = false
  hasError.value = false

  if (imageRef.value) {
    imageDimensions.value = {
      width: imageRef.value.naturalWidth,
      height: imageRef.value.naturalHeight,
    }
    emit('load', imageDimensions.value)
  }
}

function handleImageError(): void {
  isLoading.value = false
  hasError.value = true
  emit('error', new Error('Failed to load image'))
}

function handleWheel(event: WheelEvent): void {
  if (!props.enableZoom) return

  if (event.deltaY < 0) {
    emit('zoomIn')
  } else {
    emit('zoomOut')
  }
}

function handleDoubleClick(): void {
  if (!props.enableZoom) return

  if (props.panZoom.isZoomed) {
    emit('resetZoom')
  } else {
    emit('zoomIn')
  }
}

function handleMouseDown(event: MouseEvent): void {
  if (!props.enablePan || !props.panZoom.isZoomed) return
  if (event.button !== 0) return // Only left click

  isDragging.value = true
  dragStart.value = { x: event.clientX, y: event.clientY }

  // Add document-level listeners
  document.addEventListener('mousemove', handleMouseMove)
  document.addEventListener('mouseup', handleMouseUp)
}

function handleMouseMove(event: MouseEvent): void {
  if (!isDragging.value) return

  const deltaX = event.clientX - dragStart.value.x
  const deltaY = event.clientY - dragStart.value.y

  dragStart.value = { x: event.clientX, y: event.clientY }
  emit('pan', deltaX, deltaY)
}

function handleMouseUp(): void {
  isDragging.value = false
  document.removeEventListener('mousemove', handleMouseMove)
  document.removeEventListener('mouseup', handleMouseUp)
}

// Touch handlers
function getTouchDistance(touches: TouchList): number {
  if (touches.length < 2) return 0
  const dx = touches[0].clientX - touches[1].clientX
  const dy = touches[0].clientY - touches[1].clientY
  return Math.sqrt(dx * dx + dy * dy)
}

function handleTouchStart(event: TouchEvent): void {
  if (event.touches.length === 2 && props.enableZoom) {
    // Pinch zoom start
    lastTouchDistance.value = getTouchDistance(event.touches)
  } else if (event.touches.length === 1 && props.enablePan && props.panZoom.isZoomed) {
    // Pan start
    isDragging.value = true
    dragStart.value = {
      x: event.touches[0].clientX,
      y: event.touches[0].clientY,
    }
  }
}

function handleTouchMove(event: TouchEvent): void {
  if (event.touches.length === 2 && props.enableZoom) {
    // Pinch zoom
    event.preventDefault()
    const newDistance = getTouchDistance(event.touches)
    if (lastTouchDistance.value > 0) {
      if (newDistance > lastTouchDistance.value * 1.1) {
        emit('zoomIn')
        lastTouchDistance.value = newDistance
      } else if (newDistance < lastTouchDistance.value * 0.9) {
        emit('zoomOut')
        lastTouchDistance.value = newDistance
      }
    }
  } else if (event.touches.length === 1 && isDragging.value) {
    // Pan
    event.preventDefault()
    const touch = event.touches[0]
    const deltaX = touch.clientX - dragStart.value.x
    const deltaY = touch.clientY - dragStart.value.y
    dragStart.value = { x: touch.clientX, y: touch.clientY }
    emit('pan', deltaX, deltaY)
  }
}

function handleTouchEnd(): void {
  isDragging.value = false
  lastTouchDistance.value = 0
}

// Cleanup
onUnmounted(() => {
  document.removeEventListener('mousemove', handleMouseMove)
  document.removeEventListener('mouseup', handleMouseUp)
})
</script>

<style lang="scss" scoped>
.image-viewer {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: transparent;
  user-select: none;

  &--dragging {
    cursor: grabbing !important;

    .image-viewer__image-wrapper {
      cursor: grabbing !important;
    }
  }

  &__loading {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 10;
  }

  &__image-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    transform-origin: center center;
    transition-property: transform;
    transition-timing-function: ease-out;
  }

  &__image {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    pointer-events: none;
  }

  &__error {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    color: rgba(var(--v-theme-on-surface), 0.75);
  }

  &__error-text {
    font-size: 14px;
  }
}

// Transitions
.image-fade-enter-active,
.image-fade-leave-active {
  transition: opacity 0.3s ease;
}

.image-fade-enter-from,
.image-fade-leave-to {
  opacity: 0;
}

.image-slide-enter-active,
.image-slide-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.image-slide-enter-from {
  transform: translateX(30px);
  opacity: 0;
}

.image-slide-leave-to {
  transform: translateX(-30px);
  opacity: 0;
}

.image-zoom-enter-active,
.image-zoom-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.image-zoom-enter-from {
  transform: scale(0.9);
  opacity: 0;
}

.image-zoom-leave-to {
  transform: scale(1.1);
  opacity: 0;
}
</style>
